import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";

import {
  createMyWebhook,
  deleteMyWebhook,
  listMyWebhooks,
  rotateMyWebhookSecret,
  testMyWebhook,
  updateMyWebhook,
} from "@/lib/partner-portal.functions";
import { WEBHOOK_EVENTS } from "@/lib/partner-scopes";
import { SiteHeader } from "@/components/site/SiteHeader";
import { PartnerNav, deliveryLabel } from "@/components/partner/PartnerNav";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Skeleton } from "@/components/ui/skeleton";

export const Route = createFileRoute("/_authenticated/partner/webhooks")({
  head: () => ({
    meta: [
      { title: "Webhooks — Portal de socios" },
      { name: "description", content: "Registra endpoints, elige eventos y prueba entregas firmadas con HMAC-SHA256." },
      { property: "og:title", content: "Webhooks — Portal de socios" },
      { property: "og:description", content: "Autogestión de webhooks para socios de distribución de Mostrando México." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: WebhooksPage,
});

function WebhooksPage() {
  const qc = useQueryClient();
  const { data: hooks, isLoading } = useQuery({ queryKey: ["partner", "webhooks"], queryFn: () => listMyWebhooks() });
  const [url, setUrl] = useState("");
  const [environment, setEnvironment] = useState<"sandbox" | "production">("sandbox");
  const [events, setEvents] = useState<string[]>(["availability.changed", "price.changed"]);
  const [secret, setSecret] = useState<string | null>(null);

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ["partner"] });
  };

  const create = useMutation({
    mutationFn: () => createMyWebhook({ data: { url, environment, events } }),
    onSuccess: (res) => {
      setSecret(res.secret);
      setUrl("");
      invalidate();
      toast.success("Webhook registrado");
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const patch = useMutation({
    mutationFn: (input: { id: string; status?: "active" | "disabled"; events?: string[] }) =>
      updateMyWebhook({ data: input }),
    onSuccess: () => {
      invalidate();
      toast.success("Webhook actualizado");
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const remove = useMutation({
    mutationFn: (id: string) => deleteMyWebhook({ data: { id } }),
    onSuccess: () => {
      invalidate();
      toast.success("Webhook eliminado");
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const rotate = useMutation({
    mutationFn: (id: string) => rotateMyWebhookSecret({ data: { id } }),
    onSuccess: (res) => {
      setSecret(res.secret);
      toast.success("Secreto rotado");
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const test = useMutation({
    mutationFn: (id: string) => testMyWebhook({ data: { id } }),
    onSuccess: (res: any) => {
      invalidate();
      if (res.delivered > 0) toast.success(`Evento ${res.event} entregado correctamente`);
      else toast.error(`No se pudo entregar el evento de prueba (${res.deliveries} intento(s))`);
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <div className="mx-auto max-w-5xl space-y-6 px-4 py-10 sm:px-6 lg:px-8">
        <h1 className="font-display text-3xl font-semibold">Webhooks</h1>
        <PartnerNav />

        {secret && (
          <div className="rounded-xl border border-primary/40 bg-primary/5 p-4">
            <div className="text-sm font-semibold">Secreto de firma — cópialo ahora</div>
            <code className="mt-2 block break-all rounded-lg bg-background p-3 font-mono text-xs">{secret}</code>
            <p className="mt-2 text-xs text-muted-foreground">
              Firma: <code className="font-mono">X-MM-Signature: t=&lt;ts&gt;,v1=HMAC_SHA256(secret, `${"{"}ts{"}"}.&lt;body&gt;`)</code>
            </p>
            <Button size="sm" className="mt-3" onClick={() => setSecret(null)}>
              Ya lo guardé
            </Button>
          </div>
        )}

        <form
          className="space-y-4 rounded-xl border bg-card p-5"
          onSubmit={(e) => {
            e.preventDefault();
            if (events.length === 0) return toast.error("Selecciona al menos un evento");
            create.mutate();
          }}
        >
          <h2 className="text-lg font-semibold">Nuevo endpoint</h2>
          <div className="grid gap-4 sm:grid-cols-[1fr_180px]">
            <div className="space-y-1.5">
              <Label htmlFor="hook-url">URL (HTTPS)</Label>
              <Input
                id="hook-url"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="https://api.tuempresa.com/hooks/mm"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="hook-env">Entorno</Label>
              <select
                id="hook-env"
                className="h-10 w-full rounded-md border bg-background px-3 text-sm"
                value={environment}
                onChange={(e) => setEnvironment(e.target.value as "sandbox" | "production")}
              >
                <option value="sandbox">sandbox</option>
                <option value="production">production</option>
              </select>
            </div>
          </div>
          <fieldset className="space-y-2">
            <legend className="text-sm font-medium">Eventos</legend>
            <div className="flex flex-wrap gap-3">
              {WEBHOOK_EVENTS.map((ev) => (
                <label key={ev} className="flex items-center gap-2 text-sm">
                  <Checkbox
                    checked={events.includes(ev)}
                    onCheckedChange={(v) => setEvents((prev) => (v ? [...prev, ev] : prev.filter((p) => p !== ev)))}
                  />
                  <span className="font-mono text-xs">{ev}</span>
                </label>
              ))}
            </div>
          </fieldset>
          <Button type="submit" disabled={create.isPending}>
            {create.isPending ? "Registrando…" : "Registrar webhook"}
          </Button>
        </form>

        <div className="rounded-xl border bg-card p-5">
          <h2 className="text-lg font-semibold">Endpoints registrados</h2>
          {isLoading ? (
            <Skeleton className="mt-3 h-24 w-full" />
          ) : (hooks ?? []).length === 0 ? (
            <p className="mt-2 text-sm text-muted-foreground">Aún no registras endpoints.</p>
          ) : (
            <ul className="mt-3 space-y-4">
              {(hooks ?? []).map((h: any) => (
                <li key={h.id} className="space-y-2 border-t pt-3">
                  <div className="break-all font-mono text-sm">{h.url}</div>
                  <div className="text-xs text-muted-foreground">
                    {h.environment} · {h.status} · {(h.events ?? []).join(", ")}
                  </div>
                  {h.lastDelivery && (
                    <div className="text-xs text-muted-foreground">
                      Última entrega: {h.lastDelivery.event_type} → {deliveryLabel(h.lastDelivery.status)} (HTTP{" "}
                      {h.lastDelivery.response_status ?? "—"}) · {new Date(h.lastDelivery.created_at).toLocaleString("es-MX")}
                    </div>
                  )}
                  <div className="flex flex-wrap gap-2">
                    <Button size="sm" variant="outline" disabled={test.isPending} onClick={() => test.mutate(h.id)}>
                      Enviar prueba
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={patch.isPending}
                      onClick={() => patch.mutate({ id: h.id, status: h.status === "active" ? "disabled" : "active" })}
                    >
                      {h.status === "active" ? "Desactivar" : "Activar"}
                    </Button>
                    <Button size="sm" variant="outline" disabled={rotate.isPending} onClick={() => rotate.mutate(h.id)}>
                      Rotar secreto
                    </Button>
                    <Button
                      size="sm"
                      variant="destructive"
                      disabled={remove.isPending}
                      onClick={() => {
                        if (confirm("¿Eliminar este webhook?")) remove.mutate(h.id);
                      }}
                    >
                      Eliminar
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
